import { ThunkAction } from 'redux-thunk';
import axios from '../../utils/axios';
import { api, api2, cookies, paths } from '../apis';
import { Profile } from 'types';
import {
  AUTH_SET_PROFILE,
  AUTH_UPDATE_AVATAR,
  AUTH_UPDATE_AVATAR_URL,
  AUTH_UPDATE_EMAIL,
  AUTH_UPDATE_NAME,
  AUTH_UPDATE_PASSWORD,
  AUTH_REQUEST_PROFILE,
  AUTH_FAILURE_REQUEST,
  AUTH_FAILURE_UPDATE,
  AUTH_UPDATE_PROFILE,
  AUTH_RESET_MESSAGE,
  AUTH_LOGOUT,
  AUTH_GOVT_ID,
} from 'store/types/auth.type';
import { State } from 'store/reducers/auth.reducer';

export const logout = (sessionTimeOut = false) => {
  cookies.remove('authToken');
  cookies.remove('userInfo');
  let a = document.createElement('a');
  a.href = (process.env.REACT_APP_LOGIN_URL || 'https://staging.chartrequest.com/') + `?logout=1${sessionTimeOut ? '&auto_timeout=1' : ''}`;
  setTimeout(() => {
    a.click();
    a.remove();
  }, 0);
  return { type: AUTH_LOGOUT };
};
export const receiveProfile = (data: Profile) => ({
  type: AUTH_SET_PROFILE,
  data,
});

export const passwordUpdated = (error: string, success: string) => ({
  type: AUTH_UPDATE_PASSWORD,
  error, success
});

export const updateProfile = () => ({
  type: AUTH_UPDATE_PROFILE,
});

export const requestProfile = () => ({
  type: AUTH_REQUEST_PROFILE,
});

export const updateFailure = (error: string) => ({
  type: AUTH_FAILURE_UPDATE,
  error,
});

export const requestFailure = (error: string) => ({
  type: AUTH_FAILURE_REQUEST,
  error,
});

export const receiveName = (names: any) => ({
  type: AUTH_UPDATE_NAME,
  names,
});

export const updateLocalAvatar = () => ({
  type: AUTH_UPDATE_AVATAR,
});

export const receiveEmail = (email: string) => ({
  type: AUTH_UPDATE_EMAIL,
  email,
});

export const resetMessage = () => ({
  type: AUTH_RESET_MESSAGE
})

const updateAvatarBlobUrl = (url: string) => ({
  type: AUTH_UPDATE_AVATAR_URL,
  url,
});

const govtIdData = (payload: any) => ({ type: AUTH_GOVT_ID, payload })

export type Actions =
  | ReturnType<typeof requestProfile>
  | ReturnType<typeof requestFailure>
  | ReturnType<typeof updateLocalAvatar>
  | ReturnType<typeof updateFailure>
  | ReturnType<typeof updateProfile>
  | ReturnType<typeof receiveProfile>
  | ReturnType<typeof receiveName>
  | ReturnType<typeof receiveEmail>
  | ReturnType<typeof passwordUpdated>
  | ReturnType<typeof resetMessage>
  | ReturnType<typeof updateAvatarBlobUrl>
  | ReturnType<typeof govtIdData>
  | ReturnType<typeof logout>;

type ThunkResult<R> = ThunkAction<R, State, undefined, Actions>;
const updateAvatarUrl = (): ThunkResult<void> => dispatch => {
  axios
    .get(paths.userAvatar, { responseType: 'blob' })
    .then(res => res.data)
    .then(blob => {
      let url = URL.createObjectURL(blob);
      dispatch(updateAvatarBlobUrl(url));
    })
    .catch(err => null);
};

export const profileFetch = (token?: string | undefined | null, callback?: () => void): ThunkResult<void> => async dispatch => {
  if (token) {
    dispatch(requestProfile());
    api
      .get(paths.userInfo, { 'authorization-token': token })(dispatch, true, true)
      .then(res => {
        cookies.save('authToken', token);
        cookies.save('userInfo', { ...res.user, organization: res.organization, govtIdRequired: res.require_government_id });
        dispatch(receiveProfile({ ...res.user, organization: res.organization, govtIdRequired: res.require_government_id }));
        dispatch(updateAvatarUrl());
        callback && callback()
      })
      .catch(error => dispatch(logout()));
  } else {
    let userInfo = cookies.getUserInfo();
    if (userInfo) {
      dispatch(receiveProfile(userInfo));
      dispatch(updateAvatarUrl());
      callback && callback()
    } else dispatch(logout());
  }
};

export const updateName = (data: any): ThunkResult<void> => async dispatch => {
  dispatch(updateProfile());
  api
    .post(paths.userInfo, { user: data })(dispatch)
    .then(res => {
      cookies.updateUser(data);
      dispatch(receiveName(data));
    })
    .catch(error => dispatch(updateFailure(error.message)));
};

export const updateEmail = (newEmail: string): ThunkResult<void> => async dispatch => {
  dispatch(updateProfile());
  api
    .post(paths.userInfo, { user: { email: newEmail } })(dispatch)
    .then(res => {
      cookies.updateUser({ email: newEmail });
      dispatch(receiveEmail(newEmail));
    })
    .catch(error => dispatch(updateFailure(error)));
};

export const updateAvatar = (data: any): ThunkResult<void> => async dispatch => {
  dispatch(updateProfile());
  api
    .post(paths.userInfo, { user: { avatar: data } })(dispatch)
    .then(res => {
      dispatch(updateLocalAvatar());
      dispatch(updateAvatarUrl());
    })
    .catch(error => dispatch(updateFailure(error)));
};

export const updatePassword = (password: string, new_password: string): ThunkResult<void> => async dispatch => {
  dispatch(updateProfile());
  api
    .put('/api/v2/user/update_password', { password, new_password })(dispatch)
    .then(res => dispatch(passwordUpdated('', 'Password updated successfully!')))
    .catch(err => dispatch(passwordUpdated(err.message, '')));
};


export const reportBug = (fd: FormData, callback: (error: boolean, message: string) => void): ThunkResult<void> => async dispatch => {
  api2
    .post('/jira', fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })(dispatch)
    .then(res => callback(false, res.message))
    .catch(err => callback(true, err.message));
};

export const saveGovtId = (fd: any, callback?: () => void): ThunkResult<void> => async dispatch => {
  dispatch(updateProfile());
  api2
    .patch('/api/v2/government_documents/update', fd)(dispatch)
    .then(res => {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      callback?.()
      dispatch(fetchGovtId())
    })
    .catch(err => dispatch(updateFailure(err)));
};
export const deleteGovtId = (fd: any, callback?: () => void): ThunkResult<void> => async dispatch => {
  dispatch(updateProfile());
  api2
    .delete('/api/v2/government_documents/destroy', fd)(dispatch)
    .then(() => {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      callback?.()
      dispatch(fetchGovtId())
    })
    .catch(err => dispatch(updateFailure(err)));
};
export const fetchGovtId = (): ThunkResult<void> => async dispatch => {
  return new Promise((resolve, reject) => {
    dispatch(updateProfile());
    api2
      .get('/api/v2/government_documents/show')(dispatch)
      .then(res => {
        dispatch(govtIdData(res.government_documents));
        resolve(res.government_documents); // Resolve the promise with the data
      })
      .catch(err => {
        dispatch(updateFailure(err));
        reject(err); // Reject the promise with the error
      });
  });
};
